export function SkuGuide() {
  return (
    <div className="rounded-lg border bg-card p-6 space-y-6 text-sm max-w-3xl">
      <section>
        <h2 className="text-lg font-semibold">What is a SKU?</h2>
        <p className="text-muted-foreground mt-2">
          A SKU is the canonical record for any product or service ATEK sells, stocks,
          or invoices. It&apos;s the join key between this app, QuickBooks, Medusa, and
          HubSpot — so every product has exactly one SKU and every system points back
          to it.
        </p>
      </section>

      <section>
        <h3 className="font-semibold">The 3 SKU types</h3>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li>
            <strong>Inventory</strong> — physical goods we stock. Track BOM, suppliers,
            and on-hand quantity in WMS.
          </li>
          <li>
            <strong>Non-Inventory</strong> — physical goods we resell but don&apos;t stock
            (drop-ship, custom orders).
          </li>
          <li>
            <strong>Service</strong> — labour, calibrations, installations,
            subscriptions. No stock, no BOM.
          </li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold">Naming convention</h3>
        <p className="text-muted-foreground mt-2">
          Transmitter SKUs follow this pattern:
        </p>
        <pre className="mt-2 rounded bg-muted px-3 py-2 text-xs overflow-x-auto">
          {'ATEK-T-{communication}-{sensor}'}
        </pre>
        <ul className="list-disc pl-5 mt-3 space-y-1 text-muted-foreground">
          <li>
            <strong>Communication codes:</strong> <code>WF</code> (Wi-Fi),{' '}
            <code>LTE</code>, <code>LR</code> (LoRa)
          </li>
          <li>
            <strong>Sensor codes:</strong> <code>digital</code>, <code>TH</code>{' '}
            (temp+humidity), <code>RTD</code>, <code>O2</code>, <code>CO2</code>,{' '}
            <code>LN2</code>
          </li>
        </ul>
        <p className="text-muted-foreground mt-3">Examples:</p>
        <ul className="list-disc pl-5 mt-1 space-y-1 text-muted-foreground font-mono text-xs">
          <li>ATEK-T-WF-TH</li>
          <li>ATEK-T-LTE-RTD</li>
          <li>ATEK-T-LR-CO2</li>
        </ul>
        <p className="text-muted-foreground mt-3">
          Convention for non-transmitter SKUs (services, accessories) is being
          finalized — coordinate with operations before creating one.
        </p>
      </section>

      <section>
        <h3 className="font-semibold">Required fields per type</h3>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li>
            <strong>All types:</strong> sku, type, English name, French name, activated
          </li>
          <li>
            <strong>Inventory:</strong> + price, cost, manufacturer, suppliers (BOM if
            assembly), photos
          </li>
          <li>
            <strong>Non-Inventory:</strong> + price, cost, suppliers
          </li>
          <li>
            <strong>Service:</strong> + price, sales account
          </li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold">BOM (Inventory only)</h3>
        <p className="text-muted-foreground mt-2">
          A Bill of Materials lists the component SKUs that are consumed when producing
          or assembling this SKU. Add components on the BOM tab; MOQ rolls up from the
          suppliers of each component.
        </p>
      </section>

      <section>
        <h3 className="font-semibold">Suppliers</h3>
        <p className="text-muted-foreground mt-2">
          A SKU can have multiple suppliers. The primary supplier&apos;s price drives
          the SKU&apos;s cost field; alternates are stored for fallback. Track lead time
          and MOQ per supplier so purchasing can pick the right one.
        </p>
      </section>

      <section>
        <h3 className="font-semibold">External system links</h3>
        <p className="text-muted-foreground mt-2">
          QuickBooks linking via the QuickBook ID is live (see the Accounting tab).
          Medusa and HubSpot links — and the side-by-side Compare and catalog-wide Sync
          — ship in upcoming releases.
        </p>
      </section>
    </div>
  );
}
